<script setup lang="ts">
import { useVariablesForMentions } from '@/composables/queries/variables/useVariablesForMentions'

defineOptions({ inheritAttrs: false })

const { t } = useI18n()
const { variables, tooltipLabels, missingBadgeTooltip, invalidBadgeTooltip } =
  useVariablesForMentions()

// soft limit: counter + warning only, no truncation on input or paste
const { maxLength } = defineProps<{
  maxLength?: number
}>()

const modelValue = defineModel<string | undefined>({ required: true })

const internalValue = computed({
  get: () => modelValue.value,
  set: (val) => {
    modelValue.value = val
  },
})

const charCount = computed(() => internalValue.value?.length ?? 0)

const wrapperRef = ref<HTMLElement | null>(null)

function insertTextWithLineBreaks(range: Range, text: string) {
  const fragment = document.createDocumentFragment()
  const lines = text.split('\n')
  lines.forEach((line, i) => {
    if (line) fragment.appendChild(document.createTextNode(line))
    if (i < lines.length - 1) fragment.appendChild(document.createElement('br'))
  })
  const lastNode = fragment.lastChild
  range.insertNode(fragment)
  if (lastNode) {
    range.setStartAfter(lastNode)
    range.collapse(true)
  }
}

function handlePaste(event: Event) {
  const e = event as ClipboardEvent
  // Always intercept to properly insert <br> nodes for newlines
  // (the library inserts raw text nodes whose \n doesn't render in contenteditable)
  const pasted = e.clipboardData?.getData('text/plain') ?? ''
  if (!pasted) return
  e.preventDefault()
  e.stopPropagation()
  const selection = window.getSelection()
  if (!selection?.rangeCount) return
  const range = selection.getRangeAt(0)
  range.deleteContents()
  insertTextWithLineBreaks(range, pasted)
  selection.removeAllRanges()
  selection.addRange(range)
  ;(e.target as HTMLElement).dispatchEvent(new Event('input', { bubbles: true }))
}

onMounted(async () => {
  await nextTick()
  wrapperRef.value?.querySelector('[contenteditable]')?.addEventListener('paste', handlePaste, true)
})

onUnmounted(() => {
  wrapperRef.value
    ?.querySelector('[contenteditable]')
    ?.removeEventListener('paste', handlePaste, true)
})
</script>

<template>
  <div ref="wrapperRef">
    <OTextAreaMentionsBadge
      v-bind="$attrs"
      v-model="internalValue"
      :options="variables"
      :dropdown-title="t('variables.dropdown_title')"
      :tooltip-labels="tooltipLabels"
      :missing-badge-tooltip="missingBadgeTooltip"
      :error-badge-tooltip="invalidBadgeTooltip"
    />
    <div v-if="maxLength" class="flex justify-end text-xs mt-1">
      <span class="text-gray-400">{{ charCount }}/{{ maxLength }}</span>
    </div>
  </div>
</template>
